import { Box, Button, DialogTitle, Divider, Drawer } from "@mui/material";
import { useState } from "react";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import type { FundInvestorContact, InvestorPortalRoleWithNoneValue } from "../../../../api/types/investorContactsTypes";
import type { ContactFundUpdate } from "./contactChangesState";
import PermissionsFields from "./PermissionsFields";

interface Props {
  contact: FundInvestorContact;
  pendingUpdate: ContactFundUpdate | undefined;
  onApply: (role: InvestorPortalRoleWithNoneValue, categoryExternalIds: string[]) => void;
  onClose: () => void;
}

const EditContactDrawer = ({ contact, pendingUpdate, onApply, onClose }: Props) => {
  const [role, setRole] = useState<InvestorPortalRoleWithNoneValue>(
    pendingUpdate ? pendingUpdate.role : (contact.role ?? "None")
  );
  const [selectedCategoryExternalIds, setSelectedCategoryExternalIds] = useState(
    pendingUpdate?.categoryExternalIds ?? contact.categoryExternalIds
  );

  const handleToggleCategory = (categoryExternalId: string) =>
    setSelectedCategoryExternalIds((previous) =>
      previous.includes(categoryExternalId)
        ? previous.filter((id) => id !== categoryExternalId)
        : [...previous, categoryExternalId]
    );

  const handleSetAllCategories = (allCategoryExternalIds: string[], selected: boolean) =>
    setSelectedCategoryExternalIds(selected ? allCategoryExternalIds : []);

  const handleApply = () => onApply(role, selectedCategoryExternalIds);

  // An empty selection is only appliable against a no-access original: it reverts a staged attach or is a no-op
  const revertsToNoAccess =
    role === "None" && (contact.role ?? "None") === "None" && contact.categoryExternalIds.length === 0;

  return (
    <Drawer anchor="right" open onClose={onClose} sx={{ "& .MuiDrawer-paper": { width: "32rem" } }}>
      <DialogTitle>Edit 1 contact</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <Divider />
      <Box sx={{ py: 2.5, px: 3, flexGrow: 1, overflow: "auto" }}>
        <PermissionsFields
          role={role}
          roleDisabled={contact.isCurrentUser}
          roleOptions={["None", "Member", "Admin"]}
          selectedCategoryExternalIds={selectedCategoryExternalIds}
          onRoleChange={setRole}
          onToggleCategory={handleToggleCategory}
          onSetAllCategories={handleSetAllCategories}
        />
      </Box>
      <Divider />
      <DialogButtons onCancel={onClose}>
        <Button
          variant="contained"
          disabled={selectedCategoryExternalIds.length === 0 && !revertsToNoAccess}
          onClick={handleApply}
        >
          Apply
        </Button>
      </DialogButtons>
    </Drawer>
  );
};

export default EditContactDrawer;
